/**
 * Plain-English names for the identifiers the API speaks in.
 *
 * The admin screens never show a key: an audit action, a permission, a
 * notification type and a MIME type are all vocabulary for the system, not for
 * the front desk. Every lookup falls back to a readable rendering of the key
 * itself, so a value added to the backend tomorrow degrades to
 * "Rate card version created" rather than disappearing or showing raw.
 */

/** "rate_card.version_created" → "Rate card version created". */
function humanise(key: string, dropDomain = true): string {
  const parts = key.split('.');
  const words = (dropDomain && parts.length > 1 ? parts.slice(1) : parts)
    .join(' ')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

const ACTIONS: Record<string, string> = {
  'admin.user.created': 'Administrator added',
  'admin.user.updated': 'Administrator edited',
  'admin.user.roles_replaced': 'Roles changed',
  'admin.user.sessions_revoked': 'Sessions signed out',
  'auth.admin_login_succeeded': 'Signed in',
  'auth.admin_login_failed': 'Sign-in refused',
  'auth.admin_logout': 'Signed out',
  'auth.admin_otp_issued': 'Sign-in code sent',
  'auth.admin_otp_resent': 'Sign-in code resent',
  'auth.admin_otp_failed': 'Sign-in code refused',
  'auth.password_reset_requested': 'Password reset requested',
  'auth.password_reset_completed': 'Password reset',
  'auth.password_reset_failed': 'Password reset refused',
  'auth.password_reset_request_ignored': 'Password reset ignored',
  'auth.password_reset_request_suppressed': 'Password reset held back',
  'communications.email_template.created': 'Email template added',
  'communications.email_template.updated': 'Email template edited',
  'communications.email_template.version_created': 'Email draft saved',
  'communications.email_template.version_activated': 'Email version made live',
  'crm.customer.created': 'Customer added',
  'crm.customer.updated': 'Customer edited',
  'crm.customer.deleted': 'Customer deleted',
  'facilities.resource.created': 'Space added',
  'facilities.resource.updated': 'Space edited',
  'facilities.desk.status_changed': 'Desk taken in or out of service',
  'reservations.booking.created': 'Booking made',
  'reservations.booking.requested': 'Booking requested',
  'reservations.booking.cancelled': 'Booking cancelled',
  'payments.booking.recorded': 'Payment recorded',
  'expense.created': 'Expense recorded',
  'expense.revised': 'Expense edited',
  'file.upload_initiated': 'Upload started',
  'file.upload_completed': 'File uploaded',
  'file.upload_rejected': 'Upload rejected',
  'file.deleted': 'File deleted',
  'file.restored': 'File restored',
  'file.purged': 'File purged',
  'file.renamed': 'File renamed',
  'file.moved': 'File moved',
  'file.visibility_changed': 'File visibility changed',
  'file_folder.created': 'Folder added',
  'file_folder.renamed': 'Folder renamed',
  'file_folder.moved': 'Folder moved',
  'file_folder.deleted': 'Folder deleted',
  'pricing.rate_card.version_created': 'Rate card published',
};

/** What happened, in the words an administrator would use. */
export const actionLabel = (action: string) => ACTIONS[action] ?? humanise(action);

const ENTITIES: Record<string, string> = {
  auth_attempt: 'Sign-in attempt',
  customer_profile: 'Customer',
  desk: 'Desk',
  email_template: 'Email template',
  expense: 'Expense',
  file: 'File',
  file_folder: 'Folder',
  otp_challenge: 'Sign-in code',
  password_reset_token: 'Password reset',
  booking: 'Booking',
  rate_card: 'Rate card',
  resource: 'Space',
  user: 'Administrator',
};

/** What the entry was about. */
export const entityLabel = (type: string) => ENTITIES[type] ?? humanise(type, false);

const NOTIFICATIONS: Record<string, string> = {
  'auth.admin_login_otp': 'Sign-in code',
  'auth.admin_password_reset': 'Password reset link',
};

/** What the mail was, rather than what the outbox calls it. */
export const notificationLabel = (type: string) => NOTIFICATIONS[type] ?? humanise(type);

const CHANNELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

/** How it was sent. */
export const channelLabel = (channel: string) => CHANNELS[channel] ?? humanise(channel, false);

const PERMISSIONS: Record<string, string> = {
  'admin.users.read': 'view administrators',
  'admin.users.manage': 'add and edit administrators',
  'applications.read': 'view careers applications and CVs',
  'applications.manage': 'review careers applications and add staff notes',
  'audit.read': 'read the audit log',
  'communications.read': 'view email templates and deliveries',
  'communications.manage': 'edit email templates',
  'communications.send': 'send mail',
  'content.read': 'view files and managed content',
  'content.manage': 'upload files and edit managed content',
  'content.publish': 'publish managed content',
  'crm.customers.read': 'view customers',
  'crm.customers.manage': 'add, edit and delete customers',
  'crm.customers.export': 'export customers',
  'events.read': 'view the events programme',
  'events.manage': 'create and edit events',
  'events.checkin': 'check attendees in at the door',
  'expenses.read': 'view expenses and their history',
  'expenses.manage': 'record and edit expenses',
  'facilities.read': 'view spaces and pricing',
  'facilities.manage': 'edit spaces and pricing',
  'payments.read': 'view the payment ledger',
  'payments.record': 'record payments',
  'payments.adjust': 'adjust payments',
  'reservations.read': 'view bookings and availability',
  'reservations.manage': 'make and cancel bookings',
  'reservations.override': 'override booking rules',
  'surveys.read': 'view surveys and their responses',
  'surveys.manage': 'enter, edit and delete survey responses',
  'surveys.design': 'create surveys and change their questions',
};

/**
 * What the permission lets someone do, phrased to drop into a sentence:
 * "You need permission to …".
 */
export const permissionLabel = (key: string) =>
  PERMISSIONS[key] ?? humanise(key, false).toLowerCase();

const FILE_TYPES: Record<string, string> = {
  'image/jpeg': 'JPEG image',
  'image/png': 'PNG image',
  'image/webp': 'WebP image',
  'application/pdf': 'PDF document',
};

/** "image/png" → "PNG image". */
export const fileTypeLabel = (mimeType: string) =>
  FILE_TYPES[mimeType] ?? mimeType.split('/').pop()?.toUpperCase() ?? mimeType;

const ROLES: Record<string, string> = {
  super_admin: 'Super administrator',
};

/** A role by name. Audit entries carry only the key, so it is spelled out here. */
export const roleLabel = (key: string) => ROLES[key] ?? humanise(key, false);

const ACTORS: Record<string, string> = {
  admin: 'Administrator',
  customer: 'Customer',
  system: 'The system',
};

/** Who did it, when there is no name on the record. */
export const actorLabel = (type: string) => ACTORS[type] ?? humanise(type, false);

/** "previousVersionNumber" → "Previous version number". */
export const fieldLabel = (key: string) => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .toLowerCase()
    .trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
};

function readable(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(readable).filter((v): v is string => v !== null);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
}

/** Identifiers carry no meaning for a reader, so they never make it out. */
const isIdentifier = (key: string) =>
  /(^|_|.)(id|ids|key|keys|uuid|token)$|Id$|Ids$|Key$|Keys$/.test(key);

/**
 * Whatever extra the API recorded, as rows a person can read.
 *
 * Audit and delivery records carry a free-form bag of detail. Printing it as
 * JSON puts the API's own field names on screen; this spells them out instead
 * and flattens one level of nesting, which is as deep as these bags go.
 */
export function describeDetail(
  detail: Record<string, unknown>,
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  for (const [key, value] of Object.entries(detail)) {
    if (isIdentifier(key)) continue;
    const flat = readable(value);
    if (flat !== null) {
      rows.push({ label: fieldLabel(key), value: flat });
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [innerKey, innerValue] of Object.entries(value as Record<string, unknown>)) {
        if (isIdentifier(innerKey)) continue;
        const inner = readable(innerValue);
        if (inner !== null) {
          rows.push({ label: `${fieldLabel(key)} · ${fieldLabel(innerKey)}`, value: inner });
        }
      }
    }
  }

  return rows;
}
